
import React, { useState } from "react";
import useFinancialTransactions from "../../hooks/useFinancialTransactions";
import { TRANSACTION_TYPES } from "../../constants/transactionTypes";
import TransactionForm from "./TransactionForm";
import TransactionTable from "./TransactionTable";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import styles from "./RevenueTab.module.css";

const MONTHS = [
  "يناير",
  "فبراير",
  "مارس",
  "أبريل",
  "مايو",
  "يونيو",
  "يوليو",
  "أغسطس",
  "سبتمبر",
  "أكتوبر",
  "نوفمبر",
  "ديسمبر",
];

const RevenueTab = () => {
  const {
    transactions,
    loading,
    error,
    createTransaction,
    updateTransaction,
    deleteTransaction,
    filters,
    setFilters,
  } = useFinancialTransactions(
    TRANSACTION_TYPES.REVENUE
  );

  // =====================================================
  // Form
  // =====================================================

  const [editingItem, setEditingItem] =
    useState(null);

  const [showForm, setShowForm] =
    useState(false);

  // =====================================================
  // Delete
  // =====================================================

  const [itemToDelete, setItemToDelete] =
    useState(null);

  const [deleting, setDeleting] =
    useState(false);

  // =====================================================
  // Date Filter
  // =====================================================

  const [showDateFilter, setShowDateFilter] =
    useState(false);

  /*
   * day | month | year
   */
  const [filterType, setFilterType] =
    useState("day");

  /*
   * هل بنحدد From ولا To؟
   */
  const [selectingField, setSelectingField] =
    useState("From");

  /*
   * التواريخ المؤكدة
   */
  const [fromDate, setFromDate] =
    useState(null);

  const [toDate, setToDate] =
    useState(null);

  /*
   * التاريخ المؤقت داخل الـ Modal
   */
  const [tempDate, setTempDate] =
    useState(null);

  const [selectedYear, setSelectedYear] =
    useState(new Date().getFullYear());

  const [selectedMonth, setSelectedMonth] =
    useState(new Date().getMonth());

  const [selectedDay, setSelectedDay] =
    useState(new Date().getDate());

  // =====================================================
  // Helpers
  // =====================================================

  const pad = (value) =>
    String(value).padStart(2, "0");

  const formatDisplayDate = (date) => {
    if (!date) return "";

    return `${pad(date.getDate())}/${pad(
      date.getMonth() + 1
    )}/${date.getFullYear()}`;
  };

  const formatApiStart = (date) => {
    return `${date.getFullYear()}-${pad(
      date.getMonth() + 1
    )}-${pad(date.getDate())}T00:00:00`;
  };

  const formatApiEnd = (date) => {
    return `${date.getFullYear()}-${pad(
      date.getMonth() + 1
    )}-${pad(date.getDate())}T23:59:59`;
  };

  // =====================================================
  // Open Date Modal
  // =====================================================

  const openDateFilter = (field) => {
    setSelectingField(field);

    const currentDate =
      field === "From"
        ? fromDate
        : toDate;

    const dateToUse =
      currentDate || new Date();

    const copiedDate = new Date(
      dateToUse
    );

    setTempDate(copiedDate);

    setSelectedYear(
      copiedDate.getFullYear()
    );

    setSelectedMonth(
      copiedDate.getMonth()
    );

    setSelectedDay(
      copiedDate.getDate()
    );

    setShowDateFilter(true);
  };

  // =====================================================
  // Temporary Date
  // =====================================================

  const selectTempDate = (date) => {
    if (!date) return;

    setTempDate(date);

    setSelectedYear(
      date.getFullYear()
    );

    setSelectedMonth(
      date.getMonth()
    );

    setSelectedDay(
      date.getDate()
    );
  };

  // =====================================================
  // Day
  // =====================================================

  const handleSelectDay = (day) => {
    const date = new Date(
      selectedYear,
      selectedMonth,
      day
    );

    selectTempDate(date);
  };

  // =====================================================
  // Month
  // =====================================================

  const handleSelectMonth = (
    monthIndex
  ) => {
    let date;

    if (selectingField === "From") {
      /*
       * بداية الشهر
       */
      date = new Date(
        selectedYear,
        monthIndex,
        1
      );
    } else {
      /*
       * نهاية الشهر
       */
      date = new Date(
        selectedYear,
        monthIndex + 1,
        0
      );
    }

    selectTempDate(date);
  };

  // =====================================================
  // Year
  // =====================================================

  const handleSelectYear = (year) => {
    let date;

    if (selectingField === "From") {
      /*
       * بداية السنة
       */
      date = new Date(
        year,
        0,
        1
      );
    } else {
      /*
       * نهاية السنة
       */
      date = new Date(
        year,
        11,
        31
      );
    }

    selectTempDate(date);
  };

  // =====================================================
  // Confirm Date
  // =====================================================

  const confirmDateSelection = () => {
    if (!tempDate) return;

    if (selectingField === "From") {
      setFromDate(
        new Date(tempDate)
      );
    } else {
      setToDate(
        new Date(tempDate)
      );
    }

    /*
     * هنا فقط الـ Modal يقفل.
     */
    setShowDateFilter(false);
  };

  // =====================================================
  // Cancel
  // =====================================================

  const cancelDateSelection = () => {
    setShowDateFilter(false);
  };

  // =====================================================
  // Apply Filter
  // =====================================================

  const applyDateFilter = () => {
    if (!fromDate || !toDate) {
      alert(
        "من فضلك اختر التاريخ من وإلى"
      );

      return;
    }

    if (fromDate > toDate) {
      alert(
        "تاريخ البداية يجب أن يكون قبل تاريخ النهاية"
      );

      return;
    }

    /*
     * هنا فقط نحدث الـ filters.
     *
     * الـ hook هيعمل GET جديد.
     */
    setFilters((prev) => ({
      ...prev,

      From: formatApiStart(
        fromDate
      ),

      To: formatApiEnd(
        toDate
      ),

      TransactionType:
        TRANSACTION_TYPES.REVENUE,

      PageIndex: 1,
    }));
  };

  // =====================================================
  // Clear Filter
  // =====================================================

  const clearDateFilter = () => {
    setFromDate(null);
    setToDate(null);
    setTempDate(null);

    setFilters((prev) => ({
      ...prev,

      From: "",
      To: "",

      TransactionType:
        TRANSACTION_TYPES.REVENUE,

      PageIndex: 1,
    }));
  };

  // =====================================================
  // Form Submit
  // =====================================================

  const handleSubmit = async (data) => {
    if (editingItem) {
      await updateTransaction(
        editingItem.id,
        data
      );
    } else {
      await createTransaction(data);
    }

    setShowForm(false);
    setEditingItem(null);
  };

  // =====================================================
  // Edit
  // =====================================================

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowForm(true);
  };

  // =====================================================
  // Delete
  // =====================================================

  const handleDelete = (item) => {
    setItemToDelete(item);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      setDeleting(true);

      await deleteTransaction(
        itemToDelete.id
      );

      setItemToDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const cancelDelete = () => {
    if (deleting) return;

    setItemToDelete(null);
  };

  // =====================================================
  // Calendar
  // =====================================================

  const daysInMonth =
    new Date(
      selectedYear,
      selectedMonth + 1,
      0
    ).getDate();

  const currentYear =
    new Date().getFullYear();

  /*
   * 5 سنوات
   */
  const years = Array.from(
    { length: 5 },
    (_, index) =>
      currentYear - index
  );

  // =====================================================
  // Render
  // =====================================================

  return (
    <div
      className={styles.container}
      dir="rtl"
    >
      {/* =================================================
          HEADER
      ================================================== */}

      <div className={styles.header}>
        <div>
          <h3>الإيرادات</h3>

          <p className={styles.subtitle}>
            إدارة ومتابعة الإيرادات
          </p>
        </div>

        <button
          type="button"
          onClick={() => {
            setEditingItem(null);
            setShowForm(true);
          }}
        >
          + إضافة إيراد
        </button>
      </div>

      {/* =================================================
          DATE FILTER
      ================================================== */}

      <div className={styles.filterBox}>
        {/* From */}

        <div className={styles.dateField}>
          <label>من</label>

          <button
            type="button"
            className={styles.dateButton}
            onClick={() =>
              openDateFilter("From")
            }
          >
            {fromDate
              ? formatDisplayDate(
                  fromDate
                )
              : "اختر تاريخ البداية"}
          </button>
        </div>

        {/* To */}

        <div className={styles.dateField}>
          <label>إلى</label>

          <button
            type="button"
            className={styles.dateButton}
            onClick={() =>
              openDateFilter("To")
            }
          >
            {toDate
              ? formatDisplayDate(
                  toDate
                )
              : "اختر تاريخ النهاية"}
          </button>
        </div>

        {/* Buttons */}

        <div
          className={
            styles.filterActions
          }
        >
          <button
            type="button"
            className={
              styles.applyButton
            }
            onClick={
              applyDateFilter
            }
          >
            تطبيق
          </button>

          <button
            type="button"
            className={
              styles.clearButton
            }
            onClick={
              clearDateFilter
            }
          >
            مسح
          </button>
        </div>
      </div>

      {/* =================================================
          SELECTED RANGE
      ================================================== */}

      <div
        className={
          styles.selectedRange
        }
      >
        <span>
          الفترة المختارة:
        </span>

        <strong>
          {fromDate
            ? formatDisplayDate(
                fromDate
              )
            : "غير محدد"}

          <span
            className={
              styles.arrow
            }
          >
            {" → "}
          </span>

          {toDate
            ? formatDisplayDate(
                toDate
              )
            : "غير محدد"}
        </strong>
      </div>

      {/* =================================================
          ERROR
      ================================================== */}

      {error && (
        <p className={styles.error}>
          {error}
        </p>
      )}

      {/* =================================================
          FORM
      ================================================== */}

      {showForm && (
        <TransactionForm
          initialData={editingItem}
          onSubmit={handleSubmit}
          onCancel={() => {
            setShowForm(false);
            setEditingItem(null);
          }}
          submitLabel={
            editingItem
              ? "تحديث"
              : "إضافة"
          }
        />
      )}

      {/* =================================================
          TABLE
      ================================================== */}

      <TransactionTable
        transactions={transactions}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      {/* =================================================
          DELETE CONFIRM
      ================================================== */}

      <ConfirmDialog
        isOpen={!!itemToDelete}
        title="تأكيد الحذف"
        message={`هل أنت متأكد من حذف "${
          itemToDelete?.title ||
          itemToDelete?.name ||
          "هذا العنصر"
        }"؟ لا يمكن التراجع عن هذا الإجراء.`}
        loading={deleting}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />

      {/* =================================================
          DATE MODAL
      ================================================== */}

      {showDateFilter && (
        <div
          className={
            styles.modalOverlay
          }
          onClick={
            cancelDateSelection
          }
        >
          <div
            className={styles.modal}
            onClick={(event) =>
              event.stopPropagation()
            }
          >
            {/* ===========================================
                HEADER
            ============================================ */}

            <div
              className={
                styles.modalHeader
              }
            >
              <div>
                <h3>
                  اختيار{" "}
                  {selectingField ===
                  "From"
                    ? "تاريخ البداية"
                    : "تاريخ النهاية"}
                </h3>

                <p>
                  اختر اليوم أو الشهر أو
                  السنة ثم اضغط تأكيد
                </p>
              </div>

              <button
                type="button"
                className={
                  styles.closeButton
                }
                onClick={
                  cancelDateSelection
                }
              >
                ×
              </button>
            </div>

            {/* ===========================================
                TYPE
            ============================================ */}

            <div
              className={
                styles.typeTabs
              }
            >
              <button
                type="button"
                className={
                  filterType === "day"
                    ? styles.activeTab
                    : ""
                }
                onClick={() =>
                  setFilterType("day")
                }
              >
                يوم
              </button>

              <button
                type="button"
                className={
                  filterType === "month"
                    ? styles.activeTab
                    : ""
                }
                onClick={() =>
                  setFilterType("month")
                }
              >
                شهر
              </button>

              <button
                type="button"
                className={
                  filterType === "year"
                    ? styles.activeTab
                    : ""
                }
                onClick={() =>
                  setFilterType("year")
                }
              >
                سنة
              </button>
            </div>

            {/* ===========================================
                DAY
            ============================================ */}

            {filterType === "day" && (
              <>
                <div
                  className={
                    styles.calendarHeader
                  }
                >
                  <button
                    type="button"
                    onClick={() => {
                      const previous =
                        new Date(
                          selectedYear,
                          selectedMonth -
                            1,
                          1
                        );

                      setSelectedYear(
                        previous.getFullYear()
                      );

                      setSelectedMonth(
                        previous.getMonth()
                      );
                    }}
                  >
                    ‹
                  </button>

                  <strong>
                    {
                      MONTHS[
                        selectedMonth
                      ]
                    }{" "}
                    {selectedYear}
                  </strong>

                  <button
                    type="button"
                    onClick={() => {
                      const next =
                        new Date(
                          selectedYear,
                          selectedMonth +
                            1,
                          1
                        );

                      setSelectedYear(
                        next.getFullYear()
                      );

                      setSelectedMonth(
                        next.getMonth()
                      );
                    }}
                  >
                    ›
                  </button>
                </div>

                <div
                  className={
                    styles.dayGrid
                  }
                >
                  {Array.from(
                    {
                      length:
                        daysInMonth,
                    },
                    (_, index) => {
                      const day =
                        index + 1;

                      const isSelected =
                        tempDate &&
                        tempDate.getFullYear() ===
                          selectedYear &&
                        tempDate.getMonth() ===
                          selectedMonth &&
                        tempDate.getDate() ===
                          day;

                      return (
                        <button
                          key={day}
                          type="button"
                          className={
                            isSelected
                              ? `${styles.dayButton} ${styles.selected}`
                              : styles.dayButton
                          }
                          onClick={() =>
                            handleSelectDay(
                              day
                            )
                          }
                        >
                          {day}
                        </button>
                      );
                    }
                  )}
                </div>
              </>
            )}

            {/* ===========================================
                MONTH
            ============================================ */}

            {filterType === "month" && (
              <>
                <div
                  className={
                    styles.calendarHeader
                  }
                >
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedYear(
                        (prev) =>
                          prev - 1
                      )
                    }
                  >
                    ‹
                  </button>

                  <strong>
                    {selectedYear}
                  </strong>

                  <button
                    type="button"
                    onClick={() =>
                      setSelectedYear(
                        (prev) =>
                          prev + 1
                      )
                    }
                  >
                    ›
                  </button>
                </div>

                <div
                  className={
                    styles.monthGrid
                  }
                >
                  {MONTHS.map(
                    (
                      month,
                      index
                    ) => {
                      const isSelected =
                        tempDate &&
                        tempDate.getFullYear() ===
                          selectedYear &&
                        tempDate.getMonth() ===
                          index;

                      return (
                        <button
                          key={month}
                          type="button"
                          className={
                            isSelected
                              ? `${styles.monthButton} ${styles.selected}`
                              : styles.monthButton
                          }
                          onClick={() =>
                            handleSelectMonth(
                              index
                            )
                          }
                        >
                          {month}
                        </button>
                      );
                    }
                  )}
                </div>
              </>
            )}

            {/* ===========================================
                YEAR
            ============================================ */}

            {filterType === "year" && (
              <div
                className={
                  styles.yearGrid
                }
              >
                {years.map(
                  (year) => {
                    const isSelected =
                      tempDate &&
                      tempDate.getFullYear() ===
                        year;

                    return (
                      <button
                        key={year}
                        type="button"
                        className={
                          isSelected
                            ? `${styles.yearButton} ${styles.selected}`
                            : styles.yearButton
                        }
                        onClick={() =>
                          handleSelectYear(
                            year
                          )
                        }
                      >
                        {year}
                      </button>
                    );
                  }
                )}
              </div>
            )}

            {/* ===========================================
                FOOTER
            ============================================ */}

            <div
              className={
                styles.modalFooter
              }
            >
              <div
                className={
                  styles.tempSelectedDate
                }
              >
                <span>
                  الاختيار الحالي:
                </span>

                <strong>
                  {tempDate
                    ? formatDisplayDate(
                        tempDate
                      )
                    : "لم يتم الاختيار"}
                </strong>
              </div>

              <div
                className={
                  styles.modalActions
                }
              >
                <button
                  type="button"
                  className={
                    styles.cancelModalButton
                  }
                  onClick={
                    cancelDateSelection
                  }
                >
                  إلغاء
                </button>

                <button
                  type="button"
                  className={
                    styles.confirmModalButton
                  }
                  disabled={!tempDate}
                  onClick={
                    confirmDateSelection
                  }
                >
                  تأكيد
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RevenueTab;
